// Intercept and refresh expired tokens for multiple requests (same implementation but with some abstractions)
//
// HOW TO USE:
// import applyAppTokenRefreshInterceptor from 'axios.refresh_token.2.js';
// import axios from 'axios';
// ...
// applyAppTokenRefreshInterceptor(axios); // register the interceptor with all axios instance
// ...
// - Alternatively:
// const apiClient = axios.create({baseUrl: 'example.com/api'});
// applyAppTokenRefreshInterceptor(apiClient); // register the interceptor with one specific axios instance
// ...
// - With custom options:
// applyAppTokenRefreshInterceptor(apiClient, {
//      shouldIntercept: (error) => {
//          return error.response.data.errorCode === 'EXPIRED_ACCESS_TOKEN';
//      }
// ); // register the interceptor with one specific axios instance
//
// PS: You may need to figure out some minor things yourself as this is just a proof of concept and not a tutorial.
// Forgive me in advance

import axios from 'axios';
import {API_URL, setApiAuthBearer} from "./Api";
import {createLocalAuthData} from "../sagas/Auth";
import {setUserToken} from "../actions";
import {store} from "../MainApp";
import {bbShortenString} from "./bbHelpers";

const CODES_THAT_CAUSE_REFRESH_CALL = [
    //removing the 401 trigger because causing more problems than solving, specifically on bad u/pw login case
    // 401, //Adding this back because during new user workflow, the token will require refreshing a few times.
    // 402, //known to handle well but is for payments by convention
    427, //expected to be handled well
];

const shouldIntercept = (error) => {
    try {
        return CODES_THAT_CAUSE_REFRESH_CALL.includes(error.response.status);
    } catch (e) {
        return false;
    }
};

const setTokenData = (tokenData = {}, axiosClient) => {
    console.log("setTokenData running with tokenData", tokenData, "axiosClient", axiosClient);

    // If necessary: save to storage
    //   tokenData's content includes data from handleTokenRefresh(): {
    //     idToken: data.auth_token,
    //     refreshToken: data.refresh_token,
    //     expiresAt: data.expires_in,
    // };
};

const handleTokenRefresh = () => {
    console.group("%chandleTokenRefresh Attempting to refresh the token...", "color:yellow");
    const refreshToken = JSON.parse(localStorage.getItem('refreshToken'));
    const token = JSON.parse(localStorage.getItem('token'));
    console.log("handleTokenRefresh refreshToken is", bbShortenString(refreshToken, 80, true), "and current token is", bbShortenString(token,80,true));
    if(!refreshToken) {
        console.warn("handleTokenRefresh the refreshToken appears to be null and that means the next /refresh call may use the wrong Auth Bearer", refreshToken);
        // return Promise.reject("handleTokenRefresh Cannot initiate a token refresh because refreshToken not defined.");
    }
    // else {
        return new Promise((resolve, reject) => {
            axios.post(`${API_URL}refresh`, {}, {
                headers: {
                    'Authorization' : `Bearer ${refreshToken}` //this is a special refresh, so use the refresh token here
                }
            })
                .then(({data}) => {
                    console.log("%chandleTokenRefresh request appears to have succeeded", "color:green");
                    console.groupEnd();
                    const tokenData = {
                        idToken: data.access_token,
                        refreshToken: data.refresh_token,
                        // expiresAt: data.expires_at,
                    };
                    resolve(tokenData);
                })
                .catch((err) => {
                    console.log("%chandleTokenRefresh Got error:", "color:red");
                    console.log("handleTokenRefresh error", err);
                    console.log("handleTokenRefresh userSignOutByTimeout is currently disabled. No further action from RefreshToken.");
                    console.groupEnd();
                    // store.dispatch(userSignOutByTimeout());
                    reject(err);
                })
        });
    // }

};

const attachTokenToRequest = (request, token) => {
    //jfd:now that there is a new token, set it in the local and store

    console.group("attachTokenToRequest is running with request", request, "token", token);

    //TODO the next lines to set the token may need to be put into setTokenData function instead
    console.log("attachTokenToRequest next line is setApiAuthBearer(token);")
    setApiAuthBearer(token); //adjust the auth header for the api object
    console.log("attachTokenToRequest next line is createLocalAuthData(token);")
    createLocalAuthData(token); //update the local storage
    console.log("attachTokenToRequest next line is setUserToken(token);")
    //TODO: jfd for some reason this action appears to fire but not getting picked up by reducer. Investigate. State is not being updated.
    // setUserToken(token); //update the state
    store.dispatch(setUserToken(token)); //trying the same function but using dispatch to see if this fixes it
    console.groupEnd();

    //this line was in original library file, may be redundant
    request.headers['Authorization'] = 'Bearer ' + token;

    // If there is an edge case where access token is also set in request query,
    // this is also a nice place to add it
    // Example: /orders?token=xyz-old-token
    if (/\/orders/.test(request.url)) {
        request.params.token = token;
    }
};

export default (axiosClient, customOptions = {}) => {
    let isRefreshing = false;
    let failedQueue = [];

    const options = {
        attachTokenToRequest,
        handleTokenRefresh,
        setTokenData,
        shouldIntercept,
        ...customOptions,
    };
    const processQueue = (error, token = null) => {
        failedQueue.forEach(prom => {
            if (error) {
                prom.reject(error);
            } else {
                prom.resolve(token);
            }
        });

        failedQueue = [];
    };

    const interceptor = (error) => {
        if (!options.shouldIntercept(error)) {
            return Promise.reject(error);
        }

        if (error.config._retry || error.config._queued) {
            return Promise.reject(error);
        }

        const originalRequest = error.config;
        if (isRefreshing) {
            return new Promise(function (resolve, reject) {
                failedQueue.push({resolve, reject})
            }).then(token => {
                originalRequest._queued = true;
                options.attachTokenToRequest(originalRequest, token);
                return axiosClient.request(originalRequest);
            }).catch(err => {
                return Promise.reject(error); // Ignore refresh token request's "err" and return actual "error" for the original request
            })
        }

        originalRequest._retry = true;
        isRefreshing = true;
        //jfd note 16 jun 22 this next block appears to be properly sending the API request /refresh
        return new Promise((resolve, reject) => {
            options.handleTokenRefresh.call(options.handleTokenRefresh)
                .then((tokenData) => {
                    options.setTokenData(tokenData, axiosClient);
                    options.attachTokenToRequest(originalRequest, tokenData.idToken);
                    processQueue(null, tokenData.idToken);
                    resolve(axiosClient.request(originalRequest));
                })
                .catch((err) => {
                    processQueue(err, null);
                    reject(err);
                })
                .finally(() => {
                    isRefreshing = false;
                })
        });
    };

    axiosClient.interceptors.response.use(undefined, interceptor);
};